import React, { useEffect, useRef, useState } from "react";
import "./MergeGame.css";
import {
    cardShowsValue,
    CELL_COUNT,
    clearTile,
    madeLine,
    MergeLevel,
    MergeResult,
    MergeState,
    MergeTarget,
    mergeTiles,
    Power,
    powerNotation,
    resultOf,
    startGame,
} from "./mergeGame.ts";
import { getRandomInt } from "../../Shared/Helpers.tsx";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

/*
 * Factor Merge's board and its three target cards (ADR 0128). The rules are
 * mergeGame.ts; this is the screen: which tile is picked, the clear mode, the
 * message line (also the live region that says why a merge was refused and what
 * a target was made of), focus, and the small flight of one tile into another.
 *
 * Built in the first render, from the engine's own start, so the board is there
 * when the page paints and nothing moves when it arrives (ADR 0067). The engine
 * draws from the random source here, never inside a state updater (ADR 0053 §8).
 *
 * The tiles are one tab stop with roving focus: arrows move to the next tile
 * along a row or down a column (empty cells are skipped), Home and End to the
 * first and last. A tile is a button named by its number; Space or Enter picks
 * it, as a tap does. Esc puts a picked tile back, or leaves clear mode.
 */

interface MergeGameBoardProps {
    level: MergeLevel;
    /** Called once, from the move that leaves no merge on the board. */
    onFinish(result: MergeResult): void;
}

/** How long the flying tile is kept before it is taken away, if its animation never reports. */
const FLIGHT_MS = 400;

interface Flight {
    key: number;
    value: number;
    left: number;
    top: number;
    width: number;
    height: number;
    dx: number;
    dy: number;
}

/** The nearest cell to `index` that holds a tile, by reading order, or -1 if the board is empty. */
function nearestTile(cells: MergeState["cells"], index: number): number {
    for (let distance = 0; distance < CELL_COUNT; distance++) {
        for (const candidate of [index + distance, index - distance]) {
            if (candidate >= 0 && candidate < CELL_COUNT && cells[candidate]) {
                return candidate;
            }
        }
    }
    return -1;
}

/** The tile an arrow key goes to from `from`, or null if there is none that way. */
function arrowTarget(cells: MergeState["cells"], from: number, key: string): number | null {
    const step = ({ ArrowRight: 1, ArrowLeft: -1, ArrowDown: 4, ArrowUp: -4 } as Record<string, number>)[key];
    if (step === undefined) {
        return null;
    }
    for (let i = from + step; i >= 0 && i < CELL_COUNT; i += step) {
        if (cells[i]) {
            return i;
        }
    }
    return null;
}

function prefersReducedMotion(): boolean {
    return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function MergeGameBoard({ level, onFinish }: MergeGameBoardProps) {
    const t = useTranslation();
    const [game, setGame] = useState<MergeState>(() => startGame(level, getRandomInt));
    const [picked, setPicked] = useState<number | null>(null);
    const [clearMode, setClearMode] = useState(false);
    // `spoken` is the line for assistive tech when the visible one has superscripts (grade 8).
    const [said, setSaid] = useState<{ text: string; spoken?: string }>({ text: "" });
    const setMessage = (text: string, spoken?: string) => setSaid({ text, spoken });
    const [focusCell, setFocusCell] = useState(0);
    const [focusTick, setFocusTick] = useState(0);
    const [shake, setShake] = useState<{ cell: number; n: number } | null>(null);
    const [flight, setFlight] = useState<Flight | null>(null);

    const boardRef = useRef<HTMLDivElement>(null);
    const cellRefs = useRef(new Map<number, HTMLButtonElement>());
    const activeRef = useRef(-1);

    const activeCell = game.cells[focusCell] ? focusCell : nearestTile(game.cells, focusCell);
    activeRef.current = activeCell;

    // A move can replace the focused button (each new tile is a new element), so focus follows by hand.
    useEffect(() => {
        if (focusTick > 0 && activeRef.current >= 0) {
            cellRefs.current.get(activeRef.current)?.focus({ preventScroll: true });
        }
    }, [focusTick]);

    useEffect(() => {
        if (!flight) {
            return undefined;
        }
        const timer = setTimeout(() => setFlight(null), FLIGHT_MS);
        return () => clearTimeout(timer);
    }, [flight]);

    function targetText(target: MergeTarget): string {
        // Grades 3-5 cards are just the number; grades 6-8 say what to make of it.
        if (cardShowsValue(level)) {
            return String(target.value);
        }
        if (target.kind === "lcm") {
            return format(t("flashCards.mergeTargetLcm"), { a: target.a!, b: target.b! });
        }
        if (target.kind === "lcm3") {
            return format(t("flashCards.mergeTargetLcm3"), { a: target.a!, b: target.b!, c: target.c! });
        }
        if (target.kind === "gcf") {
            return format(t("flashCards.mergeTargetGcf"), { a: target.a!, b: target.b! });
        }
        if (target.kind === "power") {
            // Grade 8: "Make 2⁴ × 3". The card's value is for the child to work out.
            return format(t("flashCards.mergeTargetMake"), { n: powerNotation(target.powers!) });
        }
        return format(t("flashCards.mergeTargetMake"), { n: target.value });
    }

    /**
     * A grade 8 card or line read aloud, built from the card's factors, not from the
     * superscripts: "2 to the power 4 times 3", "6 squared", "3 cubed".
     */
    function spokenPowers(powers: Power[]): string {
        return powers
            .map(([base, exponent]) => {
                if (exponent === 1) {
                    return String(base);
                }
                if (exponent === 2) {
                    return format(t("flashCards.mergeSpokenSquared"), { base });
                }
                if (exponent === 3) {
                    return format(t("flashCards.mergeSpokenCubed"), { base });
                }
                return format(t("flashCards.mergeSpokenPower"), { base, exponent });
            })
            .join(t("flashCards.mergeSpokenTimes"));
    }

    /** The spoken form of a grade 8 card, or undefined for a card with nothing to respell. */
    function targetSpoken(target: MergeTarget): string | undefined {
        return target.kind === "power"
            ? format(t("flashCards.mergeTargetMake"), { n: spokenPowers(target.powers!) })
            : undefined;
    }

    /** The spoken form of the message for a target made on a grade 8 card. */
    function spokenFor(after: MergeState): string | undefined {
        const event = after.lastEvent;
        if (event.kind !== "made" || event.target.kind !== "power") {
            return undefined;
        }
        const line = format(t("flashCards.mergeSpokenEquals"), {
            powers: spokenPowers(event.target.powers!),
            value: event.product,
        });
        return format(t("flashCards.mergeMadeTemplate"), { line });
    }

    function listText(values: number[]): string {
        const [a, b, c] = [...new Set(values)];
        if (b === undefined) {
            return String(a);
        }
        return c === undefined
            ? format(t("flashCards.mergeListTwo"), { a, b })
            : format(t("flashCards.mergeListThree"), { a, b, c });
    }

    function messageFor(before: MergeState, after: MergeState): string {
        const event = after.lastEvent;
        if (event.kind === "refused" && !cardShowsValue(level)) {
            // A grades 6-8 card can be an LCM, a GCF or a power, and the values would give its answer away.
            return format(t("flashCards.mergeRefusedAnyTemplate"), { a: event.a, b: event.b, product: event.product });
        }
        if (event.kind === "refused") {
            return format(t("flashCards.mergeRefusedTemplate"), {
                a: event.a,
                b: event.b,
                product: event.product,
                list: listText(event.targets),
            });
        }
        if (event.kind === "merged") {
            return format(t("flashCards.mergeMergedTemplate"), {
                a: before.cells[event.from]!.value,
                b: before.cells[event.to]!.value,
                product: event.product,
            });
        }
        if (event.kind === "made") {
            const { target } = event;
            const line = madeLine(target, event.product, event.parts);
            if (target.kind === "lcm") {
                return format(t("flashCards.mergeMadeLcmTemplate"), { a: target.a!, b: target.b!, value: target.value, line });
            }
            if (target.kind === "lcm3") {
                return format(t("flashCards.mergeMadeLcm3Template"), {
                    a: target.a!,
                    b: target.b!,
                    c: target.c!,
                    value: target.value,
                    line,
                });
            }
            if (target.kind === "gcf") {
                return format(t("flashCards.mergeMadeGcfTemplate"), { a: target.a!, b: target.b!, value: target.value, line });
            }
            return format(t("flashCards.mergeMadeTemplate"), { line });
        }
        if (event.kind === "cleared") {
            return format(t("flashCards.mergeClearedTemplate"), { n: event.value });
        }
        return "";
    }

    /** Starts the flight of `from` into `to`, unless the child has asked for no motion. */
    function launch(from: number, to: number, value: number) {
        const fromEl = cellRefs.current.get(from);
        const toEl = cellRefs.current.get(to);
        const board = boardRef.current;
        if (!fromEl || !toEl || !board || prefersReducedMotion()) {
            return;
        }
        const a = fromEl.getBoundingClientRect();
        const b = toEl.getBoundingClientRect();
        if (a.width === 0) {
            return;
        }
        const origin = board.getBoundingClientRect();
        setFlight({
            key: Date.now(),
            value,
            left: a.left - origin.left,
            top: a.top - origin.top,
            width: a.width,
            height: a.height,
            dx: b.left - a.left,
            dy: b.top - a.top,
        });
    }

    function settle(next: MergeState, text: string, focusAt: number, spoken?: string) {
        const ended = next.over && !game.over;
        setGame(next);
        if (next.lastEvent.kind !== "refused") {
            setShake(null);
        }
        setPicked(null);
        setClearMode(false);
        const over = t("flashCards.mergeOver");
        setMessage(
            ended ? `${text} ${over}`.trim() : text,
            spoken === undefined ? undefined : ended ? `${spoken} ${over}`.trim() : spoken
        );
        setFocusCell(nearestTile(next.cells, focusAt));
        setFocusTick((n) => n + 1);
        if (ended) {
            onFinish(resultOf(next));
        }
    }

    function press(index: number) {
        if (game.over || !game.cells[index]) {
            return;
        }
        if (clearMode) {
            const next = clearTile(game, index);
            settle(next, messageFor(game, next), index);
            return;
        }
        if (picked === null) {
            setPicked(index);
            setFocusCell(index);
            setMessage(format(t("flashCards.mergeSelectedTemplate"), { n: game.cells[index]!.value }));
            return;
        }
        if (picked === index) {
            setPicked(null);
            setMessage("");
            return;
        }
        const next = mergeTiles(game, picked, index, getRandomInt);
        if (next.lastEvent.kind === "refused") {
            setShake({ cell: index, n: (shake?.n ?? 0) + 1 });
        } else {
            launch(picked, index, game.cells[picked]!.value);
        }
        settle(next, messageFor(game, next), index, spokenFor(next));
    }

    function toggleClear() {
        if (game.over || game.clearsLeft === 0) {
            return;
        }
        setPicked(null);
        setClearMode(!clearMode);
        setMessage(clearMode ? t("flashCards.mergeClearOff") : t("flashCards.mergeClearOn"));
    }

    function onKeyDown(event: React.KeyboardEvent) {
        if (event.key === "Escape") {
            if (clearMode) {
                setClearMode(false);
                setMessage(t("flashCards.mergeClearOff"));
            } else if (picked !== null) {
                setPicked(null);
                setMessage("");
            }
            return;
        }
        const holder = (event.target as HTMLElement).closest("[data-cell]");
        if (!holder) {
            return;
        }
        const from = Number(holder.getAttribute("data-cell"));
        const to =
            event.key === "Home"
                ? nearestTile(game.cells, 0)
                : event.key === "End"
                ? nearestTile(game.cells, CELL_COUNT - 1)
                : arrowTarget(game.cells, from, event.key);
        if (to === null || to < 0) {
            return;
        }
        event.preventDefault();
        setFocusCell(to);
        cellRefs.current.get(to)?.focus({ preventScroll: true });
    }

    return (
        <div className="mergeBoard" onKeyDown={onKeyDown}>
            <p className="mergeStatus">
                {format(t("flashCards.mergeStatusTemplate"), { score: game.score, refused: game.refused })}
            </p>

            <ul className="mergeTargets" aria-label={t("flashCards.mergeTargetsLabel")}>
                {game.targets.map((target) => (
                    <li key={target.id} className={cardShowsValue(level) ? "mergeTarget mergeTarget--number" : "mergeTarget"}>
                        {targetSpoken(target) === undefined ? (
                            targetText(target)
                        ) : (
                            <>
                                <span aria-hidden="true">{targetText(target)}</span>
                                <span className="srOnly">{targetSpoken(target)}</span>
                            </>
                        )}
                    </li>
                ))}
            </ul>

            <div className="mergeBoardWrap" ref={boardRef}>
                <div className="mergeGrid" role="group" aria-label={t("flashCards.mergeBoardAria")}>
                    {game.cells.map((tile, index) => {
                        if (!tile) {
                            return <div key={`empty-${index}`} className="mergeEmpty" aria-hidden="true" />;
                        }
                        const classes = ["mergeTile"];
                        if (picked === index) {
                            classes.push("mergeTile--picked");
                        }
                        if (clearMode) {
                            classes.push("mergeTile--clearable");
                        }
                        const shaking = shake !== null && shake.cell === index;
                        if (shaking) {
                            classes.push("mergeTile--shake");
                        }
                        return (
                            <button
                                key={shaking ? `${tile.id}-${shake!.n}` : tile.id}
                                type="button"
                                className={classes.join(" ")}
                                data-cell={index}
                                aria-pressed={picked === index}
                                disabled={game.over}
                                tabIndex={index === activeCell ? 0 : -1}
                                ref={(element) => {
                                    if (element) {
                                        cellRefs.current.set(index, element);
                                    } else {
                                        cellRefs.current.delete(index);
                                    }
                                }}
                                onFocus={() => setFocusCell(index)}
                                onClick={() => press(index)}
                            >
                                {tile.value}
                            </button>
                        );
                    })}
                </div>
                {flight && (
                    <div
                        key={flight.key}
                        className="mergeGhost"
                        aria-hidden="true"
                        style={
                            {
                                left: flight.left,
                                top: flight.top,
                                width: flight.width,
                                height: flight.height,
                                "--dx": `${flight.dx}px`,
                                "--dy": `${flight.dy}px`,
                            } as React.CSSProperties
                        }
                        onAnimationEnd={() => setFlight(null)}
                    >
                        {flight.value}
                    </div>
                )}
            </div>

            <p className="mergeMessage" role="status" aria-live="polite">
                {said.spoken === undefined ? (
                    said.text
                ) : (
                    <>
                        <span aria-hidden="true">{said.text}</span>
                        <span className="srOnly">{said.spoken}</span>
                    </>
                )}
            </p>

            <div className="mergeActions">
                <button
                    type="button"
                    className={clearMode ? "btn btn-primary mergeClear mergeClear--on" : "btn btn-secondary mergeClear"}
                    aria-pressed={clearMode}
                    disabled={game.over || game.clearsLeft === 0}
                    onClick={toggleClear}
                >
                    {format(t("flashCards.mergeClearButtonTemplate"), { n: game.clearsLeft })}
                </button>
            </div>
        </div>
    );
}

export default MergeGameBoard;
